import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const MinimizedContext = createContext(null);

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

export function MinimizedProvider({ children }) {
  const [windows, setWindows] = useState([]);
  const [pending, setPending] = useState(null); // { kind, state, id }
  const pendingRef = useRef(pending); pendingRef.current = pending;

  // Push a window into the taskbar.
  const minimize = useCallback((w) => {
    const item = { id: uid(), ts: Date.now(), ...w };
    setWindows(ws => [...ws, item]);
    return item.id;
  }, []);

  // Remove a window entirely (silent — caller handles any confirm).
  const closeWindow = useCallback((id) => {
    setWindows(ws => ws.filter(w => w.id !== id));
    if (pendingRef.current?.id === id) setPending(null);
  }, []);

  // Mark a window as "pending restore" so the target page can hydrate it.
  const restore = useCallback((id) => {
    setWindows(ws => {
      const w = ws.find(x => x.id === id);
      if (w) setPending({ kind: w.kind, state: w.state, id: w.id, path: w.path });
      return ws;
    });
  }, []);

  // Target page consumes its pending restore state (kind match). Returns the
  // snapshot state or null. On successful consume, removes the window.
  const consumeRestore = useCallback((kind) => {
    const p = pendingRef.current;
    if (!p || p.kind !== kind) return null;
    const state = p.state;
    setPending(null);
    setWindows(ws => ws.filter(w => w.id !== p.id));
    return state;
  }, []);

  const value = useMemo(() => ({
    windows, pending, minimize, close: closeWindow, restore, consumeRestore,
  }), [windows, pending, minimize, closeWindow, restore, consumeRestore]);

  return <MinimizedContext.Provider value={value}>{children}</MinimizedContext.Provider>;
}

export function useMinimized() {
  const ctx = useContext(MinimizedContext);
  if (!ctx) throw new Error("useMinimized must be used inside MinimizedProvider");
  return ctx;
}
